/**
 * Single source of truth for "does this record have unpublished changes?".
 *
 * The record's `dates.changed` is only written by an update, so a record that
 * was created and published but never edited since has no `changed` at all.
 * Callers used to feed that missing value straight into moment: `moment(undefined)`
 * resolves to *now* (so lists flagged such a record as dirty forever) while
 * `diff(null)` yields NaN (so the slide-header publish button stayed inactive).
 * Falling back to `entered` — the moment the record last gained content — keeps
 * every view in agreement.
 */

export interface PublishDates {
  entered?: string | Date;
  changed?: string | Date;
  published?: string | Date;
}

/** Timestamp the record's content last changed: `changed`, else its creation date. */
export function lastContentChange(dates?: PublishDates): Date | null {
  const raw = dates?.changed || dates?.entered;
  if (!raw) {
    return null;
  }
  const date = new Date(raw);
  return Number.isNaN(date.getTime()) ? null : date;
}

/** True while the record has never been published, or has changed since it was. */
export function needsPublish(dates?: PublishDates): boolean {
  if (!dates?.published) {
    return true;
  }
  const published = new Date(dates.published);
  if (Number.isNaN(published.getTime())) {
    return true;
  }
  const changed = lastContentChange(dates);
  return changed ? changed.getTime() > published.getTime() : false;
}

/** True only for the "never published" case, which some lists render differently. */
export function neverPublished(dates?: PublishDates): boolean {
  return !dates?.published;
}
